import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play } from "lucide-react";
import { PRESTATIONS_VIDEOS } from "../mock";

export default function PrestationsVideos() {
  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 lg:py-16" data-testid="prestations-videos-page">
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-10">
        <Link to="/" className="hover:text-white">Accueil</Link>
        <span>/</span>
        <Link to="/prestations" className="hover:text-white">Événementiel</Link>
        <span>/</span>
        <span className="text-white">Vidéos</span>
      </div>

      <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
        <div className="md:col-span-8 reveal-up">
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-500 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
            Galerie · Vidéos
          </div>
          <h1 className="title-reveal font-display uppercase text-[14vw] md:text-[9vw] leading-[0.85] tracking-tight">
            Vidéos<br />
            <span className="text-blue-600">Live</span>
          </h1>
          <div className="mt-6 flex items-center gap-3">
            <span className="accent-line-red" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">Extraits de concerts & sessions</span>
          </div>
        </div>
        <div className="md:col-span-4 reveal-up" style={{ animationDelay: "120ms" }}>
          <p className="text-neutral-300 text-sm leading-relaxed">
            Sélection de captations vidéo des prestations live de <span className="text-red-500 font-medium">Vince Rodi</span>.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="videos-grid">
        {PRESTATIONS_VIDEOS.map((v, i) => (
          <div
            key={v.id}
            data-testid={`video-item-${v.id}`}
            className={`border border-white/10 bg-neutral-950 overflow-hidden ${
              v.portrait ? "md:col-span-2" : ""
            }`}
          >
            <div
              className={`bg-black relative overflow-hidden ${
                v.portrait ? "flex justify-center py-4 md:py-6" : "aspect-video"
              }`}
            >
              {v.portrait && v.poster && (
                <div
                  aria-hidden="true"
                  className="absolute inset-0 z-0"
                  style={{
                    backgroundImage: `url(${v.poster})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "blur(48px) brightness(0.55) saturate(1.1)",
                    transform: "scale(1.25)",
                  }}
                />
              )}
              {v.portrait && v.poster && (
                <div
                  aria-hidden="true"
                  className="absolute inset-0 z-0 bg-black/30"
                />
              )}
              {v.portrait ? (
                <div className="relative w-[280px] sm:w-[340px] md:w-[380px] aspect-[9/16] max-h-[80vh] z-10 shadow-2xl shadow-black/70">
                  <video
                    controls
                    poster={v.poster}
                    playsInline
                    className="w-full h-full object-contain bg-black"
                    preload="metadata"
                  >
                    <source src={v.src} type={v.type || "video/mp4"} />
                    Votre navigateur ne supporte pas la lecture vidéo HTML5.
                  </video>
                </div>
              ) : v.src ? (
                <video
                  controls
                  poster={v.poster}
                  playsInline
                  className="w-full h-full object-contain bg-black"
                  preload="metadata"
                >
                  <source src={v.src} type={v.type || "video/mp4"} />
                  Votre navigateur ne supporte pas la lecture vidéo HTML5.
                </video>
              ) : (
                <>
                  <img
                    src={v.poster}
                    alt={v.title}
                    className="w-full h-full object-cover opacity-50"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full border border-white/30 bg-black/40 backdrop-blur flex items-center justify-center">
                      <Play size={28} className="text-white/80 ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.25em] bg-red-500 text-white px-2 py-1">
                    Bientôt disponible
                  </div>
                </>
              )}
              <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] bg-black/70 backdrop-blur px-2 py-1 text-neutral-200 border border-white/10 z-10">
                {String(i + 1).padStart(2, "0")} · Vidéo
              </div>
              {v.featured && (
                <div
                  data-testid={`video-featured-badge-${v.id}`}
                  className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.3em] bg-red-500 text-white px-2.5 py-1 border border-red-500 shadow-lg shadow-red-500/30 z-10"
                >
                  ★ Vitrine
                </div>
              )}
            </div>
            <div className={`p-5 ${v.portrait ? "text-center" : ""}`}>
              <h3 className="text-lg uppercase tracking-wide text-white mb-1">{v.title}</h3>
              <p className="text-xs text-neutral-400">{v.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14 flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/prestations/photos"
          className="inline-flex items-center gap-2 border border-white/25 hover:border-white hover:bg-white hover:text-black transition-all px-5 py-3 text-xs uppercase tracking-[0.2em]"
          data-testid="to-photos-link"
        >
          Voir les photos <ArrowUpRight size={14} />
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all px-5 py-3 text-xs uppercase tracking-[0.2em]"
          data-testid="to-contact-link"
        >
          Booker une prestation <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}
